import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MedicalRecord } from '../../../models/medical-record';
import { Patient } from '../../../models/patient';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { finalize, Subject, takeUntil } from 'rxjs';
import _ from 'lodash';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { MedicalRecordService } from '../../../services/medical-record.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-medical-record-drawer',
  standalone: false,
  templateUrl: './medical-record-drawer.component.html',
  styleUrl: './medical-record-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MedicalRecordDrawerComponent implements OnInit, OnDestroy {
  @Input() edit: boolean;
  @Input() medicalRecord: MedicalRecord | undefined;
  @Input() patient: Patient;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public loading: boolean;
  public today: Date = new Date();

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
    private medicalRecordService: MedicalRecordService,
    private translate: TranslateService,
  ){}

  ngOnInit(): void {
    // load form
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      anamnesis: [this.medicalRecord?.anamnesis || '', []],
      diagnosis: [this.medicalRecord?.diagnosis || '', []],
      id: [this.medicalRecord?.id, []],
      patient: [{value: this.patient?.givenname, disabled: true}, []],
      startDate: [{value: this.medicalRecord?.startDate || this.today, disabled: this.medicalRecord?.id}, [Validators.required]],
      therapy: [this.medicalRecord?.therapy || '', []],
      title: [this.medicalRecord?.title || '', [Validators.required]],
    });

    if(this.medicalRecord) {
      this.f['id'].setValidators([Validators.required]);
    }

    if(!this.edit) {
      this.form.disable();
    }
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading = true;
      const formData = _.cloneDeep(this.form.getRawValue());
      _.set(formData, 'startDate', moment(formData.startDate).format('YYYY-MM-DD'));
      if(this.medicalRecord) {
        // update medical record
        this.medicalRecordService.updateMedicalRecord(formData, this.patient.id).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[medicalRecordService] updateMedicalRecord res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.medical-record-update'), 'success');
            this.auxiliarService.closeDrawer('left', _.get(res, 'body.data'));
          },
          error: (err: any) => {
            this.logger.error('[medicalRecordService] updateMedicalRecord err:', err);
            this.auxiliarService.toast(this.translate.instant('error.medical-record-update'), 'error');
          }
        });
      } else {
        // create medical record
        delete formData.id;
        this.medicalRecordService.createMedicalRecord(formData, this.patient.id).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[medicalRecordService] createMedicalRecord res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.medical-record-create'), 'success');
            this.auxiliarService.closeDrawer('left', _.get(res, 'body.data'));
          },
          error: (err: any) => {
            this.logger.error('[medicalRecordService] createMedicalRecord err:', err);
            this.auxiliarService.toast(this.translate.instant('error.medical-record-create'), 'error');
          }
        });
      }
    }
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('left');
  }
}
